/**
 * BridgeScene.ts
 * -----------------------------------------------------------------------------
 * Turns the geometry payload exported by the C# side
 * (BridgeGeometryExporter.ExportSceneJson) into Three.js meshes and parents
 * them under a single `root` group inside a Viewport3D.
 *
 * The C# exporter walks the VB engine's DisplayModel
 * (SubModels → GrObject → GrMesh → Quads/Triangles), converts every face to
 * triangles, and emits flat vertex positions ALREADY in viewer space:
 *   - metres (mm × 0.001)
 *   - Y-up, matching Viewport3D / Three.js (model X→x, model Z→y, model −Y→z),
 *     the same axis swap BridgeModel.WriteToOBJ uses.
 * So this module does no unit/axis conversion — it just uploads buffers.
 *
 * Positions are NON-INDEXED (three vertices per triangle, never shared), so
 * `computeVertexNormals()` yields per-face flat normals — the right look for
 * structural concrete/steel surfaces (crisp edges, no smoothing across the
 * deck/girder fillets).
 */

import * as THREE from 'three';
import type { Viewport3D } from './Viewport3D';
import { dedupePolyline, isClosedLoop, sweepPolyline, RIB_PITCH_FACTOR } from './RebarSweep';
import type { SweepOut } from './RebarSweep';
import { SelectionHull } from './SelectionHull';
import { buildCreases, disposeCreases } from './OutlineCreases';
import { NormalEdges, SURFACE_LAYER } from './NormalEdges';

/** One renderable part — a single GrMesh's surface with its fill colour. */
/** How `buildGhost` draws: colour/opacity of the see-through surface and its edges,
 *  and `exclude` to draw every structural part except the ids given. */
export interface GhostLook {
  color?: number;
  opacity?: number;
  edgeColor?: number;
  edgeOpacity?: number;
  exclude?: boolean;
}

export interface BridgePart {
  /** Component label (e.g. "Span-1", "Bent-2 Column-1"). Used as object name. */
  name: string;
  /** Selection key — the owning bridge object's UiID (GrMesh.ObjID). Parts whose
   *  id matches an Explorer selection are highlighted. */
  id?: string;
  /** "#rrggbb" fill colour from the GrMesh FillColor (0–1 RGB → hex). */
  color: string;
  /** 0–1 opacity from the fill colour alpha; < 1 renders transparent. */
  opacity: number;
  /** "surface" (triangles), "lines" (LineSegments), "rebar" (segment pairs
   *  extruded into tubes of radius dia/2) or "steel" (triangles, like a surface,
   *  but kept out of the merged bridge shell and kept opaque — see
   *  SOLID_MESH_NAMES) or "diagram" (Bridge Forces fill: triangles drawn unlit and
   *  opaque). Absent ⇒ "surface" for backward compatibility. */
  kind?: string;
  /** Surface material hint for Texture mode: "concrete" (default) or "asphalt"
   *  (traffic lanes). Ignored outside Texture mode. */
  mat?: string;
  /** Rebar tube diameter in metres (only on "rebar" parts). */
  dia?: number;
  /** Polyline encoding for bar centrelines: `polys[i]` is polyline i's vertex
   *  count and `positions` holds those vertices once, back to back. Absent ⇒
   *  `positions` is already segment-endpoint pairs (surfaces, outline edges,
   *  and payloads from before the exporter started folding bars into
   *  polylines). Reinforcement is most of a detailed model's payload and every
   *  interior vertex is shared by two segments, so the two encodings differ by
   *  very nearly a factor of two.
   *
   *  It also gates MITRING: a mitred joint has to know which two legs meet at a
   *  vertex, and endpoint pairs do not say. A part without `polys` is drawn as
   *  butt-jointed instanced cylinders however much budget is left. */
  polys?: number[];
  /** Flat vertices: triangles [x,y,z …] for surfaces, segment endpoint pairs
   *  — or polyline vertices when `polys` is set — for lines/rebar. Metres, Y-up. */
  positions: number[];
  /** Ground from an imported map (BridgeGeometryExporter tags the GroundProfile
   *  object). The viewport's terrain controls — elevation shading, wireframe,
   *  subdivision — act on these parts and leave the bridge alone. */
  terrain?: boolean;
  /** Optional per-vertex colour, r,g,b in 0..1 sRGB, one triple per vertex of
   *  `positions`. Overrides the part's flat `color` where present — which is what
   *  lets a single part carry a gradient (the imported map's terrain, shaded by
   *  elevation). Ignored in Texture mode, which has no per-vertex colour at all. */
  vcolors?: number[];
  /** Round geometry (GrMesh.IsRound — columns, piles, railing tubes): shaded
   *  smooth across faces meeting at under {@link SMOOTH_CREASE_DEG}, so a
   *  cylinder reads round while its caps and any hard corner stay crisp. */
  smooth?: boolean;
}

/** Largest angle between two faces that a `smooth` part shades across. A 32-sided
 *  tube turns 11.25° per face; a Jersey barrier's faces meet at ~30°. */
const SMOOTH_CREASE_DEG = 20;

/**
 * How `BridgeScene.select` draws the current selection.
 *
 *   'overlay'  the shipped look: the selected components' triangles redrawn in
 *              bright orange, pulled toward the camera by a polygon offset.
 *   'hull'     an inverted-hull contour per component (SelectionHull.ts) — a
 *              line round each selected component with the surface left showing
 *              its own colour.
 *   'both'     both at once, for comparing them in a single frame.
 */
export type SelectionStyle = 'overlay' | 'hull' | 'both';

/** Number of line segments a "lines" / "rebar" part carries, in either encoding. */
function segmentCount(part: BridgePart): number {
  const polys = part.polys;
  if (!polys) return Math.floor(part.positions.length / 6);
  let n = 0;
  for (let i = 0; i < polys.length; i++) n += polys[i] - 1;
  return n;
}

/**
 * Walk a part's segments, whichever encoding it arrived in, handing each one's
 * two endpoints to `cb`. Nothing is materialised on the way, so a part holding
 * a million segments costs no more than the payload it came in.
 */
function forEachSegment(
  part: BridgePart,
  cb: (ax: number, ay: number, az: number, bx: number, by: number, bz: number) => void,
): void {
  const p = part.positions;
  const polys = part.polys;

  if (!polys) {
    for (let i = 0; i + 5 < p.length; i += 6) {
      cb(p[i], p[i + 1], p[i + 2], p[i + 3], p[i + 4], p[i + 5]);
    }
    return;
  }

  let o = 0;
  for (let k = 0; k < polys.length; k++) {
    const n = polys[k];
    for (let j = 0; j + 1 < n; j++) {
      const a = o + j * 3;
      cb(p[a], p[a + 1], p[a + 2], p[a + 3], p[a + 4], p[a + 5]);
    }
    o += n * 3;
  }
}

/**
 * Walk a part's polylines, handing each one's float offset into `positions` and
 * its vertex count to `cb`. A part with no `polys` yields one two-vertex
 * polyline per endpoint pair, so a caller can stay encoding-agnostic — but note
 * a two-vertex polyline has no bend to mitre, which is why the sweep gates on
 * `polys` rather than coming through here for those.
 */
function forEachPolyline(part: BridgePart, cb: (base: number, count: number) => void): void {
  const polys = part.polys;

  if (!polys) {
    const p = part.positions;
    for (let i = 0; i + 5 < p.length; i += 6) cb(i, 2);
    return;
  }

  let o = 0;
  for (let k = 0; k < polys.length; k++) {
    const n = polys[k];
    if (n >= 2) cb(o, n);
    o += n * 3;
  }
}

/** Set once a non-finite bar vertex has been reported, so one bad model does not
 *  fill the console with a line per bar. */
let nonFiniteReported = false;

/**
 * Split a polyline at any vertex that is not a finite number, handing `cb` each
 * maximal finite run.
 *
 * The engine does occasionally emit NaN into a bar or duct centreline — a box
 * section that degenerated upstream is the usual source. The instanced path
 * absorbed it: one bad segment collapsed into one bad cylinder and that was the
 * end of it. A SWEPT tube cannot absorb it, because a ring is shared — a single
 * NaN vertex normalises to NaN, poisons the frames carried through it and drags
 * the whole strip out into a spike across the model. Screening here means bad
 * data costs the two segments either side of it and nothing more.
 */
function forEachFiniteRun(
  pos: ArrayLike<number>,
  base: number,
  count: number,
  cb: (base: number, count: number) => void,
): void {
  let runStart = -1;
  let bad = 0;
  for (let k = 0; k < count; k++) {
    const o = base + k * 3;
    const good =
      Number.isFinite(pos[o]) && Number.isFinite(pos[o + 1]) && Number.isFinite(pos[o + 2]);
    if (good) {
      if (runStart < 0) runStart = k;
      continue;
    }
    bad++;
    if (runStart >= 0 && k - runStart >= 2) cb(base + runStart * 3, k - runStart);
    runStart = -1;
  }
  if (runStart >= 0 && count - runStart >= 2) cb(base + runStart * 3, count - runStart);

  if (bad > 0 && !nonFiniteReported) {
    nonFiniteReported = true;
    console.warn(
      `[BridgeScene] a bar centreline carries ${bad} non-finite vertex(es); those runs were split ` +
        `around them. The geometry is wrong upstream of the viewer.`,
    );
  }
}

/** Full scene payload from the C# exporter. */
export interface BridgeGeometryPayload {
  units: string;
  /** Active "Color By" mode (DiplayByTypes name). "Texture" → concrete material;
   *  anything else → per-vertex fill colours. */
  colorBy?: string;
  /** Circle segments per extruded rebar/tendon tube — Settings ▸ Render Quality
   *  (Low 4, Normal 6, High 8). Absent (an older host) → 6, the original look. */
  tubeSeg?: number;
  /** True when the host built this payload with the Geometry Outline layer on
   *  (Home ▸ Visible Layers). It drives the intersection lines — see
   *  `setIntersectionLines`. Absent means OFF: a payload that never mentions the
   *  layer (the FE mesh, a hand-built scene) has no component solids for an
   *  intersection to be found between, and drawing them uninvited would cost a
   *  pass per frame for nothing. */
  geometryOutline?: boolean;
  parts: BridgePart[];
}

/** Default circle segments per rebar/tendon tube, used when the payload is silent. */
const DEFAULT_TUBE_SEGMENTS = 6;

/**
 * Meshes applyOpacity leaves alone — embedded steel stays solid so it reads THROUGH
 * transparent concrete. Two mechanisms, and both matter: skipping the fade keeps the
 * colour, and staying in the opaque pass (transparent:false, depthWrite:true) means the
 * steel writes depth first and the transparent shell is blended over it, which is what
 * makes it look like a solid object behind glass rather than a ghost.
 */
const SOLID_MESH_NAMES = new Set(['Reinforcement', 'Steel', 'Forces']);

/** Name prefix SelectionHull gives its per-component contour meshes. Used to
 *  keep them out of the opacity fade — see `applyOpacity`. */
const HULL_MESH_PREFIX = 'Hull:';

/** Clamp a payload tube-segment count to something a cylinder can be built from.
 *  Below 3 there is no cross-section, and past 16 the extra triangles buy nothing
 *  at bar scale — the host only ever sends 4 / 6 / 8. */
function tubeSegments(n: number | undefined): number {
  if (typeof n !== 'number' || !Number.isFinite(n)) return DEFAULT_TUBE_SEGMENTS;
  return Math.max(3, Math.min(16, Math.round(n)));
}

export class BridgeScene {
  /** Everything built from the payload hangs under here. */
  public readonly root = new THREE.Group();

  private readonly viewport: Viewport3D;
  private readonly disposables: Array<{ dispose(): void }> = [];

  /**
   * How many bar segments the extruded ("tube") reinforcement view will build
   * before it draws centrelines instead.
   *
   * One segment is one instance: 64 bytes of matrix here and, at Normal render
   * quality, 12 triangles on the GPU. A detailed 18-span model carries ~1.5 M of them, which is past what any
   * machine will draw interactively, and attempting it is how the tab gets
   * killed. Lines cost 24 bytes a segment in one draw call and show the same
   * bars in the same places — which is what "Rebar Display = Line" shows anyway —
   * so the fallback gives up the look, not the content.
   */
  /**
   * Triangles the extruded view may draw, on a machine rated 1.0. Converted to a segment
   * allowance at the CURRENT tube resolution by {@link tubeSegmentBudget}, because a segment is
   * `2 * seg` triangles and `seg` is the user's Render Quality - the same reasoning the mitred
   * sweep's byte budget already gives for not using a flat segment count.
   *
   * 9 M is exactly the old flat 750,000 at Normal quality (seg 6), so a baseline machine behaves
   * as before and only better hardware moves.
   */
  private static readonly BASE_TRIANGLE_BUDGET = 9_000_000;

  /** Shadow-casting allowance on a machine rated 1.0, as a fraction of the segment budget. The
   *  shadow pass re-renders every instance, and bars buried in concrete cast nothing anyone can
   *  see, so this stays a fifth of it. */
  private static readonly SHADOW_BUDGET_FRACTION = 0.2;

  /** Measured once: querying WebGL creates a context, and the answer cannot change. */
  private static deviceRating: { factor: number; label: string } | null = null;

  /**
   * How much this machine may be asked to draw, relative to the baseline.
   *
   * A flat ceiling is wrong in both directions: it wastes a workstation and still overcommits a
   * thin client. The GPU string is the strongest signal available in the page - a discrete part
   * will instance tens of millions of triangles that a software rasteriser will not attempt -
   * with memory and core count as secondary trims. All three are advisory: `deviceMemory` is
   * quantised and capped at 8 GB by the browser, the renderer string can be masked entirely, and
   * neither is a benchmark. Hence the clamp: this can only move the ceiling within a band that
   * is defensible without one.
   */
  private static rateDevice(): { factor: number; label: string } {
    if (BridgeScene.deviceRating) return BridgeScene.deviceRating;

    const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
    let gpu = '';
    try {
      const probe = document.createElement('canvas');
      const gl = (probe.getContext('webgl2') ||
                  probe.getContext('webgl')) as WebGLRenderingContext | null;
      if (gl) {
        const ext = gl.getExtension('WEBGL_debug_renderer_info');
        if (ext) gpu = String(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) ?? '');
        const lose = gl.getExtension('WEBGL_lose_context');
        if (lose) lose.loseContext();          // do not hold a second context open
      }
    } catch {
      /* blocked or unavailable - stay at the baseline */
    }

    const g = gpu.toLowerCase();
    let factor: number;
    let tier: string;
    if (/swiftshader|llvmpipe|software|basic render|microsoft basic/.test(g)) { factor = 0.2; tier = 'software'; }
    else if (/rtx|geforce|radeon rx|radeon pro|firepro|quadro|arc a|\bm[123] (pro|max|ultra)/.test(g)) { factor = 3; tier = 'discrete'; }
    else if (/intel|uhd|iris|hd graphics|vega|integrated|adreno|mali/.test(g)) { factor = 0.8; tier = 'integrated'; }
    else { factor = 1; tier = gpu ? 'unrecognised' : 'unknown'; }

    const mem = (navigator as unknown as { deviceMemory?: number }).deviceMemory;
    if (typeof mem === 'number' && mem > 0) factor *= clamp(mem / 4, 0.5, 2);
    const cores = navigator.hardwareConcurrency;
    if (typeof cores === 'number' && cores > 0) factor *= clamp(cores / 8, 0.75, 1.5);

    factor = clamp(factor, 0.2, 6);
    const label = `${tier}${gpu ? ` (${gpu})` : ''}, ` +
                  `${mem ?? '?'} GB, ${cores ?? '?'} cores -> x${factor.toFixed(2)}`;
    BridgeScene.deviceRating = { factor, label };
    console.info(`[BridgeScene] reinforcement budget rated: ${label}`);
    return BridgeScene.deviceRating;
  }

  /** Segment allowance at `radialSegments` on this machine. */
  private static tubeSegmentBudget(radialSegments: number): number {
    const tris = BridgeScene.BASE_TRIANGLE_BUDGET * BridgeScene.rateDevice().factor;
    const seg = Math.round(tris / (2 * Math.max(3, radialSegments)));
    return Math.max(200_000, Math.min(5_000_000, seg));
  }

  /**
   * Diameter (metres) at which a tube stops being allowed to look faceted.
   *
   * Faceting is a function of how many pixels the cross-section covers, not of
   * what the thing is: a 6-gon is invisible on a 16 mm bar and reads as a
   * V-shaped trough on a 65 mm post-tensioning duct. Set above the largest
   * reinforcing bar in use so ordinary rebar keeps the cheap prototype and only
   * ducts — a handful per span against millions of bars — pay for the extra
   * triangles.
   */
  private static readonly ROUND_TUBE_DIA = 0.045;

  /**
   * Most parts the full-outline experiment will contour before it starts
   * dropping them. Each one is a draw call.
   *
   * Raised from an initial 1500, which was guessed against `selectable` and was
   * biting silently on a real model: the outline source list is the wider
   * `outlineable` (id-less parts and steel solids included), so the same model
   * offers substantially more parts here than the selection list ever showed.
   * A dropped part is invisible as a bug — it just looks like the outline
   * "doesn't work on that pier" — so the cap now reports itself both to
   * `lastNotice` and to the console.
   */
  private static readonly OUTLINE_ALL_CAP = 8000;

  /** The solid meshes the intersection-line prepass may see — see SURFACE_LAYER. */
  private static readonly SURFACE_MESH_NAMES = ['BridgeModel', 'Lanes', 'Steel'];

  /** Ink colour for the full outline — both halves, so the contour and the
   *  creases read as one drawing rather than two overlaid ones. Matches the
   *  engine's own GeometryOutline edge colour closely enough that the three can
   *  be on together without looking like three different pens. */
  private static readonly OUTLINE_INK = '#241c14';

  /** Circle segments for anything past {@link ROUND_TUBE_DIA}. */
  private static readonly ROUND_TUBE_SEGMENTS = 16;

  /**
   * Buffer bytes ONE swept segment costs per facet: 3 floats of position, 3 of
   * normal and 6 Uint32 indices — so a segment is `48 * seg` bytes, 288 B at
   * Normal render quality against the 64 B of an instance matrix.
   *
   * Draw cost is NOT part of that trade: the sweep emits the same 2·seg
   * triangles a segment the cylinders do, and fewer on a capped duct (caps once
   * per polyline instead of twice per segment). Memory is the whole of it, which
   * is why the budget below is denominated in bytes.
   */
  private static readonly SWEPT_BYTES_PER_SEGMENT_FACET = 48;

  /**
   * How much buffer the MITRED sweep may ask for, on a machine rated 1.0, before ordinary bars
   * fall back to butt-jointed instanced cylinders. {@link sweptTubeByteBudget} scales it by the
   * same device rating {@link tubeSegmentBudget} uses.
   *
   * A segment count would be the wrong dial, because the cost per segment is
   * `48 * seg` and `seg` is the user's Render Quality: one number would be
   * generous at Low and reckless at High. Charging by the byte lets the same
   * ceiling mean ~666 k segments at Normal (seg 6) and ~250 k at duct quality
   * (seg 16) — and at Normal that is most of the way to
   * the instanced ceiling, so nearly anything this view will extrude at
   * all, it also mitres.
   *
   * That last sentence is the INVARIANT, and it is why this rides the device rating rather than
   * staying flat. The extrusion ceiling became x0.2-x6 of its baseline; a fixed byte budget
   * would then have left a rated-3 workstation EXTRUDING 2.25 M segments while mitring only
   * ~700 k of them, so every bend past that reopened the wedge of background the sweep exists
   * to close — silently, model-wide, and worse the better the machine. One factor moves both
   * ceilings, so the ratio between them is the same everywhere.
   *
   * 192 MB is the CPU side at the baseline; the GPU holds its own copy. That is four times what
   * the instanced path spends at its own baseline 750 k ceiling, which is the price of corners
   * that close — and being a ratio rather than an absolute, it survives the scaling.
   *
   * Past it the fat tubes are still swept — a handful of ducts per span against
   * millions of bars, and the ones a notch is most visible on.
   *
   * If this needs to come down, lowering it is the cheap lever. Half-float
   * normals would save 12.5% but need WebGL2, and chunking a bucket under 65536
   * vertices for Uint16 indices would save 25% but a quad strip needs both of
   * its rings in one geometry — neither is worth the complexity first.
   */
  private static readonly BASE_SWEPT_TUBE_BYTE_BUDGET = 192 * 1024 * 1024;

  /** Mitred-sweep buffer allowance on this machine — see {@link BASE_SWEPT_TUBE_BYTE_BUDGET}. */
  private static sweptTubeByteBudget(): number {
    return BridgeScene.BASE_SWEPT_TUBE_BYTE_BUDGET * BridgeScene.rateDevice().factor;
  }

  /** Per-part geometry kept for selection highlighting (id → flat triangle verts). */
  private selectable: Array<{ id: string; positions: number[]; ground?: boolean }> = [];
  /** Overlay mesh drawn over the currently-selected component(s). */
  private highlightMesh: THREE.Mesh | null = null;

  /**
   * EXPERIMENT: how a selection is drawn. 'overlay' is the shipped look (the
   * bright orange surface copy below); 'hull' is the inverted-hull contour in
   * SelectionHull.ts; 'both' draws them together so the two can be compared in
   * one frame. Neither affects the GeometryOutline edge lines.
   *
   * Default stays 'overlay' so nothing changes until a host flips it — see
   * `setSelectionStyle` in main.ts.
   */
  private selectionStyle: SelectionStyle = 'overlay';

  /** Built on first use, because the default style never needs it. */
  private hull: SelectionHull | null = null;

  /**
   * EXPERIMENT: a contour round EVERY component, independent of the selection —
   * the "full outline" look, as opposed to the selection contour above. Its own
   * hull instance with its own colour, because the two lines do different jobs:
   * a selection contour has to shout (orange) and a full outline has to read as
   * ink (near-black). Off by default; costs one draw call per component, which
   * is why it is capped — see OUTLINE_ALL_CAP.
   */
  private hullAll: SelectionHull | null = null;
  private outlineAll = false;

  /**
   * The crease half of the full outline: one merged LineSegments carrying every
   * part's own hard edges. The hull can only ever draw a silhouette, and on a
   * girder the silhouette is the smaller half of the drawing — see the header
   * of OutlineCreases.ts.
   */
  private creases: THREE.LineSegments | null = null;

  /** Which halves of the full outline are drawn. Both by default — "a full
   *  outline" means the contour AND the creases; split so the two can be A/B'd
   *  from the console when judging the look. */
  private outlineHullOn = true;
  private outlineCreaseOn = true;

  /** Crease threshold in degrees, tunable from the host while judging. */
  private creaseAngle = 30;

  /**
   * The third kind of line: where two solids interpenetrate. Neither the hull
   * nor the creases can draw it — there is no edge in either mesh at that curve
   * — so it is found in screen space from a normal discontinuity. See
   * NormalEdges.ts. Off by default; costs one extra pass over the surfaces per
   * drawn frame.
   */
  private normalEdges: NormalEdges | null = null;
  private intersectionsOn = false;

  /** Line width the host asked for, as a multiple of the one-pixel line, or null
   *  to keep NormalEdges' own DEFAULT_WIDTH — which is where the shipped 0.5
   *  hairline is defined, and the only place it should be written down. */
  private intersectionWidth: number | null = null;

  /** How many meshes carry SURFACE_LAYER, i.e. how much geometry the
   *  intersection-line prepass can actually see. Zero means it draws nothing —
   *  which is what happened when the layer was set on the root Group instead of
   *  the meshes, and is indistinguishable by eye from the feature being off. */
  private taggedSurfaces = 0;

  /** Every part the full outline can contour — see the note where it is built.
   *  A superset of `selectable`: id-less parts and steel solids included. */
  private outlineable: Array<{ id: string; positions: number[] }> = [];

  /** Contour look requested by the host, remembered so `setHullLook` works
   *  before anything has been selected (the hull is built lazily, so setting
   *  the look straight onto it would be writing to a material that does not
   *  exist yet and would silently reset to the default on first selection). */
  private hullColour: string | null = null;
  /** Matches SelectionHull's DEFAULT_WEIGHT — half the 2.5 px base, so the
   *  contour sits at the same visual weight as a 1 px crease line. */
  private hullWeight = 0.5;

  /** The ids passed to the last `select`, so a style change can redraw them
   *  without the host having to re-issue the selection. */
  private selectedIds: string[] = [];

  /**
   * Set when the last setGeometry drew something other than what was asked for
   * — today only the extruded-rebar downgrade. The host reads it after a load
   * and puts it on the status line, so a model whose reinforcement came back as
   * centrelines says why instead of looking like the setting was ignored.
   */
  public lastNotice: string | null = null;

  /** Off-scene mesh used only for click-picking: all selectable parts merged,
   *  with `faceObjIds` giving the owning part's id per triangle. Not rendered. */
  private pickMesh: THREE.Mesh | null = null;
  private faceObjIds: string[] = [];
  /** Bridge Forces diagrams, picked ahead of the model — see {@link pick}. */
  private diagramPickMesh: THREE.Mesh | null = null;
  private diagramFaceIds: string[] = [];
  private readonly pickRaycaster = new THREE.Raycaster();
  private readonly pickNdc = new THREE.Vector2();

  constructor(viewport: Viewport3D) {
    this.viewport = viewport;
    this.root.name = 'BridgeModel';
    viewport.scene.add(this.root);
  }

  /**
   * Replace the current geometry with a freshly exported payload.
   *
   * All parts are merged into a SINGLE opaque BufferGeometry rendered with ONE
   * material, with per-part colour carried as per-vertex colours — so the whole
   * bridge is one draw call instead of one per component.
   *
   * The bridge is rendered fully opaque: the VB engine tags most surfaces with a
   * fill-colour alpha just under 1, but that's a display tint, not intended
   * glass — honouring it makes the entire model see-through with broken depth
   * ordering. The reference modeler draws these solid, so we do too.
   */
  public setGeometry(payload: BridgeGeometryPayload): void {
    this.clear();
    this.lastNotice = null;
    nonFiniteReported = false;

    const all = payload?.parts ?? [];
    const surfaceParts = all.filter(
      (p) => (p.kind ?? 'surface') === 'surface' && p.positions && p.positions.length >= 9,
    );
    const lineParts = all.filter(
      (p) => p.kind === 'lines' && p.positions && p.positions.length >= 6,
    );
    const rebarParts = all.filter(
      (p) => p.kind === 'rebar' && p.positions && p.positions.length >= 6,
    );
    // Embedded steel solids — today the post-tensioning anchorages (bearing plate,
    // anchor head, trumpet, dead-end collar), and a tendon duct whose centreline was
    // stripped upstream. Its own bucket rather than merged into the bridge shell,
    // because the assembly sits INSIDE the concrete: an opaque shell occludes it and a
    // transparent one fades it away along with itself, so it needs the reinforcement
    // treatment instead. A separate mesh also contains the blast radius of a bad vertex
    // — one NaN makes three.js cull a whole merged geometry, and in 'BridgeModel' that
    // would be the entire bridge. Being its own kind (not a 'mat' on a surface part) is
    // what keeps it out of surfaceParts, selectable and the pick mesh with no edits to
    // any of those filters.
    const steelParts = all.filter(
      (p) => p.kind === 'steel' && p.positions && p.positions.length >= 9,
    );
    // Design ▸ Bridge Forces: flat force diagrams standing on the members. Their own
    // bucket for the same reasons as steel — kept out of the shell, the pick mesh and the
    // opacity fade, so they stay legible through a see-through deck.
    const diagramParts = all.filter(
      (p) => p.kind === 'diagram' && p.positions && p.positions.length >= 9,
    );

    // Keep surface geometry per object id so the Explorer can highlight a clicked
    // component (works in any Color By mode — the overlay is independent geometry).
    this.selectable = surfaceParts
      .filter((p) => p.id)
      .map((p) => ({ id: p.id!, positions: p.positions,
                     // Ground and road surface: not structure, so never part of the FE Mesh
                     // view's model overlay (buildGhost's `exclude`).
                     ground: !!p.terrain || p.mat === 'asphalt' || p.mat === 'grass' }));

    // EXPERIMENT: the source list for the full outline, which is NOT `selectable`.
    //
    // `selectable` answers "can the user click this and have the Explorer light
    // it up", and it is right to exclude what it excludes. A full outline asks a
    // different question — "should this have a line round it" — and the answer
    // is yes for parts the selection list drops on purpose:
    //
    //   • NO OBJECT ID. `selectable` filters on `p.id`, and the exporter emits
    //     `objId = string.Empty` when neither GrMesh.ObjID nor GrObject.ObjID is
    //     set (BridgeGeometryExporter, "Selection key"). Empty string is falsy,
    //     so those parts vanish from the list — correctly, because there is
    //     nothing to select them BY, but they are still concrete on screen. This
    //     is what left the pier caps and parts of the girder soffit unlined.
    //   • STEEL SOLIDS. `kind: 'steel'` is its own bucket and never reaches
    //     `selectable` at all — bearing plates, anchor heads, trumpets. Small,
    //     hard-edged, and exactly the parts a line drawing needs most.
    //
    // Indexed rather than keyed by object id, because two parts with no id would
    // otherwise collide under the same empty key.
    this.outlineable = [...surfaceParts, ...steelParts]
      .filter((p) => p.positions && p.positions.length >= 9)
      .map((p, i) => ({ id: p.id || `part-${i}`, positions: p.positions }));

    this.buildPickMesh();
    // A diagram carries its member's id, so a click on it reads that member's forces.
    [this.diagramPickMesh, this.diagramFaceIds] = this.buildFacePickMesh(
      diagramParts.filter((p) => p.id).map((p) => ({ id: p.id!, positions: p.positions })), THREE.DoubleSide);

    if (payload?.colorBy === 'Texture') {
      // Two materials in Texture mode: concrete for the bridge solids, asphalt
      // (+ white lane lines) for the traffic-lane planes.
      const concrete = this.buildMergedMesh(
        surfaceParts.filter((p) => p.mat !== 'asphalt' && p.mat !== 'grass'), false, 'concrete');
      if (concrete) { concrete.name = 'BridgeModel'; this.root.add(concrete); }
      const asphalt = this.buildMergedMesh(surfaceParts.filter((p) => p.mat === 'asphalt'), false, 'asphalt');
      if (asphalt) { asphalt.name = 'Lanes'; this.root.add(asphalt); }
      // Ground profile band. Its own bucket, so it gets turf rather than the concrete the
      // filter above would otherwise hand anything that is not a traffic lane.
      const grass = this.buildMergedMesh(surfaceParts.filter((p) => p.mat === 'grass'), false, 'grass');
      if (grass) { grass.name = 'Ground'; this.root.add(grass); }
    } else {
      // "Bridge Object" mode's palette (DisplayPreferences.DefaultTheme) reads a
      // little pale/washed-out once lit — darken it slightly so components read
      // with more contrast, matching the reference look. Other Color By modes
      // (Bridge, Group, …) are left at their authored colours.
      const darken = payload?.colorBy === 'BridgeObject' ? 0.75 : 1;
      const mesh = this.buildMergedMesh(surfaceParts, false, null, darken);
      if (mesh) {
        mesh.name = 'BridgeModel';
        this.root.add(mesh);
      }
    }

    const lines = this.buildMergedLines(lineParts);
    if (lines) {
      lines.name = 'BridgeOutline';
      this.root.add(lines);
    }

    // Extruded rebar (Options ▸ Rebar Display = Extruded): centreline segments
    // built into steel tubes. Line mode arrives as "lines" parts instead.
    const rebar = this.buildRebarTubes(
      rebarParts,
      tubeSegments(payload?.tubeSeg),
      payload?.colorBy === 'Texture',
    );
    if (rebar) {
      rebar.name = 'Reinforcement';
      this.root.add(rebar);
    }

    // Steel solids, in every Color By mode — texture:null even under Texture, because
    // these are steel castings and the procedural concrete map (which tiles per ~1.25 m)
    // would put a sixth of a tile on a 200 mm bearing plate and read as a concrete block.
    // One look in all modes also matches how the tendon centrelines already behave.
    const steel = this.buildMergedMesh(steelParts, false, null);
    if (steel) {
      steel.name = 'Steel'; // applyOpacity keys off this — see SOLID_MESH_NAMES
      // Match the extruded bar/duct material response rather than buildMergedMesh's
      // matte concrete default, so the anchorage and the tube it meets read as one
      // material. buildMergedMesh creates the material per call, so this is local.
      const steelMat = steel.material as THREE.MeshStandardMaterial;
      steelMat.metalness = 0.45;
      steelMat.roughness = 0.5;
      // Nothing buried in a girder casts a shadow anyone can see, and every caster is
      // re-rendered for the shadow map — the same trade buildRebarTubes makes.
      steel.castShadow = false;
      steel.receiveShadow = false;
      this.root.add(steel);
    }

    // Force diagrams, unlit: a diagram is read by its colour, and shading would darken
    // whichever side faces away from the sun. Double-sided because it is a single sheet.
    const forces = this.buildMergedMesh(diagramParts, false, null);
    if (forces) {
      forces.name = 'Forces'; // applyOpacity keys off this — see SOLID_MESH_NAMES
      (forces.material as THREE.Material).dispose();
      const forcesMat = new THREE.MeshBasicMaterial({
        vertexColors: true,
        side: THREE.DoubleSide,
        polygonOffset: true,
        polygonOffsetFactor: 1,
        polygonOffsetUnits: 1,
      });
      this.disposables.push(forcesMat);
      forces.material = forcesMat;
      forces.castShadow = false;
      forces.receiveShadow = false;
      this.root.add(forces);
    }

    // Re-apply any non-default transparency the user set (geometry was rebuilt
    // opaque), so it survives a model reload.
    // Mark the solid surfaces for the intersection-line prepass. Only these:
    // NormalEdges renders the scene with a normal override through this layer,
    // and anything else in it (crease lines, the hull, the grid, labels, the
    // camera-parented HUD) writes into the normal buffer and draws a line round
    // itself. Reinforcement is left out deliberately — a model's worth of tube
    // facets would be noise, not a drawing.
    // Traversed and matched on `isMesh`, NOT `getObjectByName`, for two reasons
    // that together made the intersection lines draw nothing at all:
    //
    //   • `getObjectByName` checks the receiver BEFORE its children
    //     (Object3D.getObjectByProperty), and `root` is itself named
    //     'BridgeModel' — so it returned the root Group and never reached the
    //     merged mesh of the same name sitting inside it.
    //   • LAYERS ARE NOT INHERITED. Three tests every object against the
    //     camera's mask individually, so a layer set on a Group does nothing
    //     whatever for its children.
    //
    // The result was an empty normal buffer, every pixel discarded as
    // background, and no line anywhere — a silent failure, because an overlay
    // that draws nothing looks exactly like an overlay that is switched off.
    let tagged = 0;
    this.taggedSurfaces = 0;
    this.root.traverse((obj) => {
      if (!(obj as THREE.Mesh).isMesh) return;
      if (!BridgeScene.SURFACE_MESH_NAMES.includes(obj.name)) return;
      obj.layers.enable(SURFACE_LAYER);
      tagged++;
    });
    this.taggedSurfaces = tagged;
    if (tagged === 0) {
      console.warn('[outline] no surface mesh tagged for the intersection-line'
        + ' prepass — intersection lines will draw nothing.');
    }

    if (this.currentOpacity < 0.999) this.applyOpacity();

    // The full outline is built from `selectable`, which `clear()` emptied at
    // the top of this method — rebuild it for the model that just arrived, so
    // the toggle survives a reload the way the transparency slider does.
    if (this.outlineAll) this.setOutlineAll(true);

    // INTERSECTION LINES FOLLOW THE GEOMETRY OUTLINE LAYER. They are the third
    // kind of outline, and the one the engine's own edge lines cannot give: a
    // GrMesh.Line exists only where the geometry has an edge, so where a bearing
    // sinks into a pier cap the drawing goes silent exactly when the outline
    // layer is the thing that was asked for. Decided here, off the payload, and
    // not from the host's toolbar: every viewport — the primary canvas, a second
    // 3D window with display settings of its own, a component preview — loads
    // through this method with the flags it was built from, so all of them agree
    // with their own Visible Layers without a line of per-page wiring.
    this.setIntersectionLines(payload?.geometryOutline === true);

    // Shadow map is on-demand in Viewport3D (autoUpdate off) — refresh once
    // now that the casters changed.
    this.viewport.refreshShadows();
  }

  /** 0–1 surface opacity for the whole model (1 = fully opaque). */
  private currentOpacity = 1;

  /**
   * A see-through copy of just the components in `ids` — the Explorer's FE Mesh
   * view draws a component's analysis elements inside it, with the rest of the
   * bridge hidden. Built from the same per-part triangles the selection uses, so
   * it is exactly the solid the component draws as. Returned detached: the caller
   * owns it (adds it to the scene, disposes it). Null when no part matches.
   */
  public buildGhost(ids: string[], look: GhostLook = {}): THREE.Group | null {
    const wanted = new Set(ids ?? []);
    // `exclude`: every structural part BUT these — the rest of the bridge, drawn round a
    // component so it reads in place.
    const take = look.exclude
      ? (p: { id: string; ground?: boolean }) => !wanted.has(p.id) && !p.ground
      : (p: { id: string }) => wanted.has(p.id);
    let count = 0;
    for (const p of this.selectable) if (take(p)) count += p.positions.length;
    if (count === 0) return null;

    const positions = new Float32Array(count);
    let at = 0;
    for (const p of this.selectable) {
      if (!take(p)) continue;
      positions.set(p.positions, at);
      at += p.positions.length;
    }
    const geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geom.computeVertexNormals();

    const surface = new THREE.Mesh(geom, new THREE.MeshStandardMaterial({
      color: look.color ?? 0xb8bcc2,
      metalness: 0,
      roughness: 0.8,
      transparent: true,
      opacity: look.opacity ?? 0.28,
      depthWrite: false,          // the mesh inside must show through every face
      side: THREE.DoubleSide,
    }));
    surface.name = 'FEGhostSurface';
    surface.renderOrder = 1;

    const g = new THREE.Group();
    g.name = look.exclude ? 'FEGhostModel' : 'FEGhost';
    g.add(surface);

    // Its hard edges, so the solid still reads as a shape at that opacity. A whole
    // bridge's worth costs a second or more to find, so a big overlay goes without.
    if (count / 9 <= 400_000) {
      const edges = new THREE.LineSegments(
        new THREE.EdgesGeometry(geom, 30),
        new THREE.LineBasicMaterial({ color: look.edgeColor ?? 0x6b7178, transparent: true,
                                      opacity: look.edgeOpacity ?? 0.55, depthWrite: false }),
      );
      edges.name = 'FEGhostEdges';
      edges.renderOrder = 2;
      g.add(edges);
    }
    return g;
  }

  /** Centre of the bounding box of the parts in `ids`, viewer space (m, Y-up); null
   *  when none match. The FE Mesh view asks where a girder's solid is, to tell which of
   *  CSiBridge's girder frame lines is that girder. */
  public partsCenter(ids: string[]): [number, number, number] | null {
    const box = this.partsBounds(ids);
    if (!box) return null;
    const c = box.getCenter(new THREE.Vector3());
    return [c.x, c.y, c.z];
  }

  /** Bounding box of the parts in `ids`, viewer space (m, Y-up); null when none match.
   *  The custom-mesh gizmo stands at the base centre of it (MeshGizmo). */
  public partsBounds(ids: string[]): THREE.Box3 | null {
    const wanted = new Set(ids ?? []);
    const box = new THREE.Box3();
    const v = new THREE.Vector3();
    for (const p of this.selectable) {
      if (!wanted.has(p.id)) continue;
      const a = p.positions;
      for (let i = 0; i + 2 < a.length; i += 3) box.expandByPoint(v.set(a[i], a[i + 1], a[i + 2]));
    }
    return box.isEmpty() ? null : box;
  }

  /** Set the model's surface opacity (0–1). Lines/outline are left opaque. */
  public setOpacity(opacity: number): void {
    this.currentOpacity = Math.max(0.05, Math.min(1, opacity));
    this.applyOpacity();
  }

  private applyOpacity(): void {
    const o = this.currentOpacity;
    const transparent = o < 0.999;
    this.root.traverse((obj) => {
      const mesh = obj as THREE.Mesh;
      if (!(mesh as unknown as { isMesh?: boolean }).isMesh) return; // skip lines
      // Keep embedded steel solid so it stays readable through transparent concrete.
      if (SOLID_MESH_NAMES.has(mesh.name)) return;
      // A contour is a line, not a surface: fading it with the model would take
      // the drawing away with the thing it is drawing, and the shared hull
      // material's depthWrite/transparent are set by setModelTransparent below
      // for reasons that have nothing to do with opacity.
      if (mesh.name.startsWith(HULL_MESH_PREFIX)) return;
      const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      for (const m of mats) {
        if (!m) continue;
        m.transparent = transparent;
        m.opacity = o;
        m.depthWrite = !transparent;
        m.needsUpdate = true;
      }
    });
    // Re-sort the contours: behind a see-through deck an inverted hull shows its
    // whole back-face shell unless it is ordered after the transparent pass.
    this.hull?.setModelTransparent(transparent);
    this.hullAll?.setModelTransparent(transparent);
  }

  /**
   * EXPERIMENT: switch how selections are drawn and redraw the current one.
   *
   * Live — the geometry for whichever style is being turned on is built here, so
   * a host can flip between them with a component already selected and compare
   * them on the same shot.
   */
  public setSelectionStyle(style: SelectionStyle): void {
    if (style === this.selectionStyle) return;
    this.selectionStyle = style;
    this.select(this.selectedIds);
  }

  /** The style currently in force. */
  public get currentSelectionStyle(): SelectionStyle {
    return this.selectionStyle;
  }

  /**
   * Live contour colour/weight. Remembered even when no hull exists yet, so a
   * host can set the look before the first selection and have it stick.
   * `colour` applies to the selection contour only — the full outline keeps its
   * ink colour, which is the point of it being a separate line.
   */
  public setHullLook(colour?: string, weight?: number): void {
    if (colour) this.hullColour = colour;
    if (weight != null) this.hullWeight = weight;
    if (this.hull) {
      if (colour) this.hull.setColour(colour);
      if (weight != null) this.hull.setWeight(weight);
    }
    if (weight != null) this.hullAll?.setWeight(weight);
  }

  /**
   * EXPERIMENT: a contour round every component, on or off. Independent of the
   * selection style — this is the "full outline" look, and it can be combined
   * with either selection style.
   *
   * COST IS REAL AND LINEAR IN COMPONENT COUNT: one draw call and one extra
   * transform of its triangles per component, plus a one-off welded-normal pass
   * over the whole model when it is switched on. That is the honest price of a
   * line between two girders that touch on screen and are the same colour —
   * the merged `BridgeModel` mesh cannot give it, because one mesh has one
   * silhouette. Capped so a very large model degrades with a notice instead of
   * locking the WebView.
   */
  public setOutlineAll(on: boolean): void {
    this.outlineAll = !!on;
    if (!this.outlineAll) {
      this.hullAll?.clear();
      if (this.hullAll?.group.parent) this.root.remove(this.hullAll.group);
      this.clearCreases();
      return;
    }
    if (!this.outlineable.length) return;

    // ---- the crease half --------------------------------------------------
    // Built first because it is the expensive one (a CPU walk of every triangle
    // in the model) and the one that shows on a flat girder web, where a hull
    // has nothing to say.
    this.clearCreases();
    if (this.outlineCreaseOn) {
      const c = buildCreases(this.outlineable, {
        angle: this.creaseAngle,
        colour: BridgeScene.OUTLINE_INK,
      });
      if (c.lines) {
        this.creases = c.lines;
        this.root.add(c.lines);
      }
      // eslint-disable-next-line no-console
      console.log(`[outline] creases: ${c.segments} segments from ${c.parts}`
        + ` of ${this.outlineable.length} parts at ${this.creaseAngle}°`
        + ` in ${c.ms.toFixed(0)} ms`);
    }

    if (!this.outlineHullOn) {
      this.hullAll?.clear();
      if (this.hullAll?.group.parent) this.root.remove(this.hullAll.group);
      return;
    }

    if (!this.hullAll) {
      // Near-black rather than orange: a full outline is ink round every
      // component, and orange everywhere would read as "everything selected".
      this.hullAll = new SelectionHull(this.viewport, { colour: BridgeScene.OUTLINE_INK });
      this.hullAll.setWeight(this.hullWeight);
      this.viewport.OnFrame(() => this.hullAll?.settle());
    }
    if (this.hullAll.group.parent !== this.root) this.root.add(this.hullAll.group);

    let parts = this.outlineable;
    let dropped = 0;
    if (parts.length > BridgeScene.OUTLINE_ALL_CAP) {
      dropped = parts.length - BridgeScene.OUTLINE_ALL_CAP;
      parts = parts.slice(0, BridgeScene.OUTLINE_ALL_CAP);
      // Appended, not assigned: setGeometry can already have left a notice here
      // (the extruded-rebar downgrade) and this runs at the end of it.
      const notice = `Full outline drawn on the first ${BridgeScene.OUTLINE_ALL_CAP}`
        + ` of ${this.outlineable.length} parts (per-part contour is one draw call`
        + ` each).`;
      this.lastNotice = this.lastNotice ? `${this.lastNotice} ${notice}` : notice;
    }
    const built = this.hullAll.set(parts);
    this.hullAll.setModelTransparent(this.currentOpacity < 0.999);
    this.hullAll.settle();

    // A contour that is missing on one pier and present on the next is
    // indistinguishable, by eye, from a contour that was never asked for on that
    // pier — so say which. `built` below `parts.length` means parts carrying no
    // triangle; `dropped` means the cap.
    // eslint-disable-next-line no-console
    console.log(`[outline] ${built} contours built from ${parts.length} parts`
      + ` (${this.outlineable.length} outlineable, ${this.selectable.length} of them`
      + ` selectable${dropped ? `, ${dropped} dropped by the ${BridgeScene.OUTLINE_ALL_CAP} cap` : ''}).`);
  }

  /** True while the full-outline experiment is on. */
  public get outlineAllOn(): boolean {
    return this.outlineAll;
  }

  /**
   * Which halves of the full outline to draw, and the crease threshold — for
   * judging the look. `hull` is the per-component silhouette, `creases` the hard
   * edges within each component; a full outline is both, and turning either off
   * is a diagnostic rather than a setting worth shipping.
   */
  public setOutlineParts(hull: boolean, creases: boolean, angle?: number): void {
    this.outlineHullOn = !!hull;
    this.outlineCreaseOn = !!creases;
    if (angle != null && angle > 0 && angle < 180) this.creaseAngle = angle;
    if (this.outlineAll) this.setOutlineAll(true);
  }

  /**
   * Intersection lines on or off, with an optional threshold in degrees (default
   * 30 — the same as the crease threshold, and for the same reason: below the
   * step between adjacent facets of a curved surface and every duct draws its
   * own faceting).
   *
   * NORMALLY DRIVEN BY THE PAYLOAD, not by a caller: `setGeometry` turns these
   * on for a model built with the Geometry Outline layer visible and off for one
   * that was not. Calling it directly overrides that until the next load, which
   * is what the console entry point in main.ts is for.
   *
   * Independent of the full outline (`setOutlineAll`): an intersection line is a
   * different kind of line from a silhouette or a crease, and the only one that
   * costs a pass per drawn frame.
   *
   * `width` is a multiple of the one-pixel line the detector draws on its own
   * (NormalEdges' DEFAULT_WIDTH — 0.5, the shipped hairline — when the host
   * says nothing). Remembered whether or not the lines are on, so it survives a
   * toggle and a model reload.
   */
  public setIntersectionLines(on: boolean, angle?: number, width?: number): void {
    this.intersectionsOn = !!on;
    if (width != null && width > 0) this.intersectionWidth = width;
    if (this.intersectionsOn) {
      if (!this.normalEdges) {
        this.normalEdges = new NormalEdges({ colour: BridgeScene.OUTLINE_INK });
        if (this.intersectionWidth != null) this.normalEdges.setWidth(this.intersectionWidth);
        // AFTER the render, not before: this composites onto the finished frame,
        // so there is nothing for it to draw on at OnFrame time.
        // Subscribed once and left in place, gated on the flag — the same shape
        // as the hull's OnFrame above, and for the same reason: BridgeScene
        // lives as long as its viewport, and an unsubscribe/resubscribe per
        // toggle is a dead callback waiting to be forgotten.
        this.viewport.OnAfterFrame((camera) => {
          if (!this.intersectionsOn || !this.normalEdges) return;
          // The camera handed in, not `viewport.camera`: the thumbnail capture
          // draws its frame with a camera of its own, and reading the live one
          // here would put the live view's lines on the thumbnail's pixels.
          this.normalEdges.render(this.viewport.renderer, this.viewport.scene, camera);
        });
      }
      if (angle != null) this.normalEdges.setAngle(angle);
      if (width != null && width > 0) this.normalEdges.setWidth(width);
      // eslint-disable-next-line no-console
      console.log(`[outline] intersection lines on — prepass sees`
        + ` ${this.taggedSurfaces} surface mesh(es).`
        + (this.taggedSurfaces === 0 ? ' Nothing will draw; reload the model.' : ''));
    } else {
      if (angle != null) this.normalEdges?.setAngle(angle);
      if (width != null && width > 0) this.normalEdges?.setWidth(width);
    }
  }

  /** True while intersection lines are on. */
  public get intersectionLinesOn(): boolean {
    return this.intersectionsOn;
  }

  private clearCreases(): void {
    if (!this.creases) return;
    this.root.remove(this.creases);
    disposeCreases(this.creases);
    this.creases = null;
  }

  /**
   * Highlight the component(s) whose object id is in `ids`. Pass an empty list
   * to clear.
   *
   * Both styles are independent geometry built from `selectable`, so both work
   * in every Color By / Texture mode.
   */
  public select(ids: string[]): void {
    this.selectedIds = [...(ids ?? [])];

    if (this.highlightMesh) {
      this.root.remove(this.highlightMesh);
      this.highlightMesh.geometry.dispose();
      (this.highlightMesh.material as THREE.Material).dispose();
      this.highlightMesh = null;
    }

    const wanted = new Set(ids ?? []);

    // ---- the contour style ------------------------------------------------
    // Built per component rather than from one merged buffer: a merged hull
    // would put a bent's two touching halves under a single silhouette and lose
    // the line between them, which is the whole reason this is not OutlinePass.
    if (this.selectionStyle === 'hull' || this.selectionStyle === 'both') {
      if (!this.hull) {
        this.hull = new SelectionHull(this.viewport);
        // The width is a fraction of the distance to the camera, so it has to be
        // restruck every frame. Viewport3D runs these callbacks immediately
        // before `renderer.render` and only when a frame is actually being
        // drawn, so an idle viewport pays nothing. Subscribed once, on the
        // instance, which is why the instance outlives a model reload.
        this.viewport.OnFrame(() => this.hull?.settle());
        if (this.hullColour) this.hull.setColour(this.hullColour);
        this.hull.setWeight(this.hullWeight);
      }
      // Re-parented here rather than kept attached across `clear`, because
      // `hasContent` counts `root`'s children to decide whether there is
      // anything to zoom to fit — an always-present group would make an empty
      // scene claim content and hand out a degenerate bounding box.
      if (this.hull.group.parent !== this.root) this.root.add(this.hull.group);
      this.hull.set(this.selectable.filter((p) => wanted.has(p.id)));
      this.hull.setModelTransparent(this.currentOpacity < 0.999);
      this.hull.settle();
    } else if (this.hull) {
      this.hull.clear();
    }

    if (wanted.size === 0) return;
    if (this.selectionStyle === 'hull') return; // contour only — no orange copy

    let floatCount = 0;
    for (const p of this.selectable) if (wanted.has(p.id)) floatCount += p.positions.length;
    if (floatCount === 0) return;

    const positions = new Float32Array(floatCount);
    let pi = 0;
    for (const p of this.selectable) {
      if (!wanted.has(p.id)) continue;
      positions.set(p.positions, pi);
      pi += p.positions.length;
    }

    const geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geom.computeVertexNormals();

    const mat = new THREE.MeshStandardMaterial({
      color: 0xff8c1a,
      emissive: 0x5a2c00, // glows a little so it reads as "selected"
      metalness: 0.0,
      roughness: 0.5,
      side: THREE.DoubleSide,
      flatShading: true,
      // Pull toward the camera so the overlay wins the depth test against the
      // original surface it sits on (no z-fighting).
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2,
    });

    const mesh = new THREE.Mesh(geom, mat);
    mesh.renderOrder = 5;
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    this.highlightMesh = mesh;
    this.root.add(mesh);
  }

  /** Build the off-scene pick mesh: every selectable part's triangles merged
   *  into one geometry, with `faceObjIds[triangleIndex]` = that part's id. Used
   *  by {@link pick} to map a ray hit back to a bridge object. */
  private buildPickMesh(): void {
    [this.pickMesh, this.faceObjIds] = this.buildFacePickMesh(this.selectable, THREE.FrontSide);
  }

  /** Merge parts' triangles into one off-scene mesh plus the owning id per triangle. */
  private buildFacePickMesh(parts: Array<{ id: string; positions: number[] }>, side: THREE.Side): [THREE.Mesh | null, string[]] {
    let floatCount = 0;
    for (const p of parts) floatCount += p.positions.length;
    if (floatCount === 0) return [null, []];

    const positions = new Float32Array(floatCount);
    const faceIds: string[] = [];
    let pi = 0;
    for (const p of parts) {
      positions.set(p.positions, pi);
      pi += p.positions.length;
      const tris = p.positions.length / 9; // 3 verts × 3 floats per triangle
      for (let t = 0; t < tris; t++) faceIds.push(p.id);
    }

    const geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    this.disposables.push(geom);

    // Positions are already world-space; keep the mesh at the origin so a raycast
    // works without it being in the scene graph. A diagram is a sheet, so it is picked
    // from both sides; the model keeps front faces only, as it always has.
    const mesh = new THREE.Mesh(geom, new THREE.MeshBasicMaterial({ side }));
    mesh.matrixAutoUpdate = false;
    mesh.updateMatrixWorld(true);
    return [mesh, faceIds];
  }

  /**
   * Pick the bridge object under a normalized-device coordinate (x,y ∈ [-1,1]).
   * Returns the part id (bridge object UiID) of the closest surface, or null.
   */
  public pick(ndcX: number, ndcY: number, camera: THREE.Camera): string | null {
    this.pickNdc.set(ndcX, ndcY);
    this.pickRaycaster.setFromCamera(this.pickNdc, camera);
    // A force diagram anywhere along the ray wins: it stands inside a see-through deck,
    // so the deck's surface is always nearer, and the diagram is what was aimed at.
    if (this.diagramPickMesh) {
      const dh = this.pickRaycaster.intersectObject(this.diagramPickMesh, false);
      if (dh.length && dh[0].faceIndex != null) {
        const id = this.diagramFaceIds[dh[0].faceIndex];
        if (id) return id;
      }
    }
    if (!this.pickMesh) return null;
    const hits = this.pickRaycaster.intersectObject(this.pickMesh, false);
    if (!hits.length || hits[0].faceIndex == null) return null;
    return this.faceObjIds[hits[0].faceIndex] ?? null;
  }

  /**
   * Like pick, with where the ray met the surface and that face's normal, turned to
   * face the camera — both world space, as the parts are. Force diagrams are not hit.
   * Used by hosts that pin things to the surface (Bridge Watch's defect pins).
   */
  public pickHit(ndcX: number, ndcY: number, camera: THREE.Camera):
      { id: string; point: [number, number, number]; normal: [number, number, number] } | null {
    if (!this.pickMesh) return null;
    this.pickNdc.set(ndcX, ndcY);
    this.pickRaycaster.setFromCamera(this.pickNdc, camera);
    const hit = this.pickRaycaster.intersectObject(this.pickMesh, false)[0];
    if (!hit || hit.faceIndex == null) return null;
    const id = this.faceObjIds[hit.faceIndex];
    if (!id) return null;
    const n = (hit.face?.normal ?? new THREE.Vector3(0, 1, 0)).clone();
    if (n.dot(this.pickRaycaster.ray.direction) > 0) n.negate();
    return { id, point: hit.point.toArray() as [number, number, number], normal: n.toArray() as [number, number, number] };
  }

  /**
   * Merge "lines" parts into LineSegments — one per distinct colour, so the
   * colour rides on the material and no per-vertex colour buffer is needed.
   * Outline and lane parts use a fixed colour, and steel used to as well — but
   * bar and duct colour now follows the host's Color By mode, so a bucket per
   * colour means one per steel-carrying object type (roughly ten to fifteen), or
   * one per span in Bridge mode, rather than the two it was. Still small: the
   * count is bounded by the palette, not by the number of bars.
   *
   * Bucketing still halves what the outline and the centreline views cost:
   * reinforcement drawn as lines on a big model is millions of vertices, and a
   * per-vertex colour array beside them is megabytes spent restating whatever
   * few colours are actually in play.
   *
   * Bar centrelines arrive as polylines and are expanded to endpoint pairs here
   * — LineSegments wants pairs, and one THREE.Line per bar would be tens of
   * thousands of draw calls.
   *
   * No normals/shadows; a tiny polygon-offset on the surface mesh keeps these
   * from z-fighting.
   */
  private buildMergedLines(parts: BridgePart[]): THREE.Group | null {
    if (!parts.length) return null;

    // Bucket by colour first, so each bucket's buffer can be sized exactly
    // rather than grown.
    const byColor = new Map<string, { parts: BridgePart[]; segments: number }>();
    for (const part of parts) {
      const segments = segmentCount(part);
      if (segments === 0) continue;
      const hex = part.color || '#202020';
      const bucket = byColor.get(hex);
      if (bucket) {
        bucket.parts.push(part);
        bucket.segments += segments;
      } else {
        byColor.set(hex, { parts: [part], segments });
      }
    }
    if (byColor.size === 0) return null;

    const group = new THREE.Group();

    for (const [hex, bucket] of byColor) {
      const positions = new Float32Array(bucket.segments * 6);
      let i = 0;
      for (const part of bucket.parts) {
        forEachSegment(part, (ax, ay, az, bx, by, bz) => {
          positions[i++] = ax; positions[i++] = ay; positions[i++] = az;
          positions[i++] = bx; positions[i++] = by; positions[i++] = bz;
        });
      }

      const geom = new THREE.BufferGeometry();
      geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      this.disposables.push(geom);

      const mat = new THREE.LineBasicMaterial({ color: new THREE.Color(hex) });
      this.disposables.push(mat);

      group.add(new THREE.LineSegments(geom, mat));
    }

    return group;
  }

  /**
   * Build extruded rebar, by one of two paths.
   *
   * The good one is a MITRED SWEEP — see {@link buildSweptMesh} and
   * RebarSweep.ts. A bar becomes one continuous tube whose rings sit in the
   * planes bisecting its bends, which is the only way the corners close.
   *
   * The cheap one is what this used to do everywhere: every bar segment becomes
   * one instance of a single shared unit cylinder, scaled to the bar's radius
   * and the segment's length and rotated onto it — one InstancedMesh per bar
   * colour, so one draw call each. It leaves a wedge of background on the
   * outside of every bend, because each cylinder is cut square to its own axis.
   * It survives as the fallback for two cases: parts that arrived as endpoint
   * pairs (no connectivity, so nothing to mitre) and ordinary bars on a model
   * whose reinforcement wants more buffer than
   * {@link BASE_SWEPT_TUBE_BYTE_BUDGET} allows this machine.
   *
   * The straightforward version — a CylinderGeometry per segment, merged at the
   * end — allocates a BufferGeometry, its attributes and a per-segment colour
   * array, and holds every one of them live until the merge, which then copies
   * the lot again. That is roughly 2 KB a segment: a model with 1.5 M bar
   * segments asks the browser for about 3 GB and is killed for it. An instance
   * is 64 bytes and draws the same picture.
   *
   * Past the machine's own ceiling even instancing is not worth attempting, and
   * the bars are drawn as centrelines instead.
   *
   * `radialSegments` is Settings ▸ Render Quality (4 / 6 / 8), and bars share one
   * prototype cylinder at that level, so it costs nothing but the triangles it
   * draws — 8 triangles a segment at Low against 16 at High. Anything at duct
   * scale ({@link ROUND_TUBE_DIA}) gets its own rounder prototype instead: see
   * that constant for why, and note it buys a second draw call, not a second
   * pass over the segments.
   */
  private buildRebarTubes(
    parts: BridgePart[],
    radialSegments: number,
    texturedMode: boolean,
  ): THREE.Object3D | null {
    if (!parts.length) return null;

    let total = 0;
    for (const part of parts) total += segmentCount(part);
    if (total === 0) return null;

    const budget = BridgeScene.tubeSegmentBudget(radialSegments);
    if (total > budget) {
      this.lastNotice =
        `Reinforcement drawn as centrelines: ${total.toLocaleString()} bar segments is past ` +
        `the ${budget.toLocaleString()} this machine will extrude at Render Quality ` +
        `${radialSegments}. Filter to one span, or lower Render Quality, to extrude them.`;
      console.warn(`[BridgeScene] ${this.lastNotice} [${BridgeScene.rateDevice().label}]`);
      return this.buildMergedLines(parts);
    }

    // The instanced fallback's prototype. Radius 1, height 1, centred on the
    // origin and running up +Y, so the instance matrix scales it to
    // (r, length, r). Open-ended: at a bend the caps sit inside the neighbouring
    // segment, and dropping them is a third of the triangles.
    //
    // One prototype per distinct segment count, built on demand: in practice that
    // is one for the bars and, on a post-tensioned model, a second for the ducts.
    // Keyed by cross-section AND by whether the ends are closed: an InstancedMesh
    // carries one geometry, so each distinct prototype needs its own bucket.
    //
    // Bars stay open-ended HERE, and only here. An uncapped shell reads as a half
    // pipe — you see the semicircular rim and straight down the inside of it — and
    // that shows on a bar as readily as on a duct, because what matters is how many
    // pixels the end covers, and the user zooms in. What made it not worth paying
    // for on this path is that an instance prototype has to carry its caps on EVERY
    // segment, doubling the triangles, to close two ends per bar.
    //
    // The sweep does not have that problem: a cap there is per bar END, so it is a
    // few triangles per bar rather than per segment, and buildSweptMesh caps
    // unconditionally. That is the same economics that made capping the ducts
    // affordable, applied to the bars.
    //
    // So this rule survives only for the fallback, where the bars are already
    // butt-jointed and the view is already degraded on purpose.
    const protos = new Map<string, THREE.CylinderGeometry>();
    const protoFor = (seg: number, capped: boolean): THREE.CylinderGeometry => {
      const key = `${seg}|${capped}`;
      let g = protos.get(key);
      if (!g) {
        g = new THREE.CylinderGeometry(1, 1, 1, seg, 1, !capped);
        // The UV attribute is dead weight in the three colour modes, but in Texture
        // mode the material samples it — and a textured material against geometry
        // with no `uv` renders undefined rather than merely untextured. CylinderGeometry
        // authors its own, 0..1 around and along, so the instanced fallback is textured
        // per SEGMENT rather than at world scale like the sweep. Acceptable: it is the
        // degraded path already, and a bar segment is short enough that one tile across
        // it reads as surface rather than as a stretched picture.
        if (!texturedMode) g.deleteAttribute('uv');
        this.disposables.push(g);
        protos.set(key, g);
      }
      return g;
    };

    // A tendon is round whatever diameter the engine reports for it. Keyed off the
    // part name and not off `dia` alone: the exporter falls back to 16 mm when a bar
    // carries no diameter, and a duct that lands on that fallback would be drawn with
    // the bar cross-section — which is exactly how a 65 mm duct came to render as a
    // square prism with a crease down it.
    const isTendon = (part: BridgePart): boolean => (part.name ?? '').endsWith('(tendon)');

    // Fat enough that the cross-section covers real pixels — the same test that
    // decides the rounder prototype also decides who keeps mitred joints once the
    // sweep budget is gone, and for the same reason: this is what gets looked at.
    const isFat = (part: BridgePart): boolean =>
      isTendon(part) || (part.dia ?? 0.02) >= BridgeScene.ROUND_TUBE_DIA;

    const segmentsFor = (part: BridgePart): number =>
      isFat(part)
        ? Math.max(radialSegments, BridgeScene.ROUND_TUBE_SEGMENTS)
        : radialSegments;

    // Mitred sweeps close the wedge of background that a butt joint leaves on the
    // outside of every bend, hence the ladder: mitre everything while the buffer
    // it wants is affordable, then mitre only the fat tubes, and past
    // the machine's segment ceiling (above) draw centrelines and none of this applies.
    //
    // Priced exactly rather than estimated: each part is charged at its OWN
    // cross-section, so a model whose ducts are 16-gons and whose bars are 6-gons
    // is not billed as if the bars were ducts.
    let sweptBytes = 0;
    // Texture mode adds a uv (2 floats a vertex) and a seam vertex per ring, so a
    // segment costs 56*seg + 32 rather than 48*seg. Charged rather than ignored,
    // because it is the budget that decides whether bars get mitred at all.
    const perFacet = texturedMode ? 56 : BridgeScene.SWEPT_BYTES_PER_SEGMENT_FACET;
    const perSegment = texturedMode ? 32 : 0;
    for (const part of parts) {
      const polys = part.polys;
      if (!polys) continue;
      const seg = segmentsFor(part);
      sweptBytes += segmentCount(part) * (seg * perFacet + perSegment);
      // Plus a flat cap at each end of each bar: seg duplicated vertices at 24 B
      // and seg-2 triangles at 12 B, twice. Around 7% on a bar of twenty segments,
      // so worth charging for rather than hand-waving.
      sweptBytes += polys.length * 2 * (seg * 24 + Math.max(0, seg - 2) * 12);
    }
    const sweepBudget = BridgeScene.sweptTubeByteBudget();
    const sweepAll = sweptBytes <= sweepBudget;
    if (sweptBytes > 0 && !sweepAll) {
      const mb = (n: number): string => (n / (1024 * 1024)).toFixed(0);
      console.info(
        `[BridgeScene] mitring every bar would want ${mb(sweptBytes)} MB of buffer, past the ` +
          `${mb(sweepBudget)} MB this machine will spend: ducts are swept, ` +
          `ordinary bars stay butt-jointed at the bends. [${BridgeScene.rateDevice().label}]`,
      );
    }

    // Bucketed by colour AND cross-section, because a bucket is one InstancedMesh
    // and an InstancedMesh carries a single geometry.
    const byBucket = new Map<string, { hex: string; seg: number; capped: boolean; parts: BridgePart[] }>();
    for (const part of parts) {
      const hex = part.color || '#c0392b';
      const seg = segmentsFor(part);
      const capped = isTendon(part);
      const key = `${hex}|${seg}|${capped}`;
      const bucket = byBucket.get(key);
      if (bucket) bucket.parts.push(part);
      else byBucket.set(key, { hex, seg, capped, parts: [part] });
    }

    const group = new THREE.Group();
    group.name = 'Reinforcement';

    const up = new THREE.Vector3(0, 1, 0);
    const dir = new THREE.Vector3();
    const mid = new THREE.Vector3();
    const scale = new THREE.Vector3();
    const quat = new THREE.Quaternion();
    const matrix = new THREE.Matrix4();

    for (const { hex, seg, capped, parts: bucketParts } of byBucket.values()) {
      let count = 0;
      for (const part of bucketParts) count += segmentCount(part);
      if (count === 0) continue;

      // A bucket is keyed by cross-section, and only fat tubes get the rounder one,
      // so `capped` and the segment count already say "this is a duct" — no need to
      // re-derive it per part.
      const isFatBucket = capped || seg >= BridgeScene.ROUND_TUBE_SEGMENTS;

      // In Texture mode steel gets its real surface: the scanned bar maps for
      // reinforcement, a metal set for ducts and anchorages. The base colour map
      // carries the material's own tone, so `color` stays white and only tints it —
      // otherwise the identity hex would be multiplied into the photograph twice.
      //
      // The maps need UVs, which the untextured paths deliberately do not carry, so
      // this is also what decides `withUv` below.
      const mat = texturedMode
        ? new THREE.MeshStandardMaterial({
            color: 0xffffff,
            map: steelTexture(isFatBucket ? 'duct' : 'rebar', 'color'),
            normalMap: steelTexture(isFatBucket ? 'duct' : 'rebar', 'normal'),
            metalness: 0.55,
            roughness: 0.55,
          })
        : new THREE.MeshStandardMaterial({
            color: new THREE.Color(hex),
            metalness: 0.45,
            roughness: 0.5,
          });
      this.disposables.push(mat);

      const castShadow = total <= BridgeScene.tubeSegmentBudget(radialSegments)
                                  * BridgeScene.SHADOW_BUDGET_FRACTION;

      // The split happens INSIDE the bucket, so both meshes share its material
      // and its cross-section and read as one object.
      const sweepParts: BridgePart[] = [];
      const instanceParts: BridgePart[] = [];
      for (const part of bucketParts) {
        if (part.polys && (sweepAll || isFat(part))) sweepParts.push(part);
        else instanceParts.push(part);
      }

      const swept = this.buildSweptMesh(sweepParts, seg, mat, castShadow, texturedMode);
      if (swept) group.add(swept);

      let instanceCount = 0;
      for (const part of instanceParts) instanceCount += segmentCount(part);
      if (instanceCount === 0) continue;

      const mesh = new THREE.InstancedMesh(protoFor(seg, capped), mat, instanceCount);
      // applyOpacity keys off this name to keep reinforcement solid behind
      // transparent concrete.
      mesh.name = 'Reinforcement';
      mesh.castShadow = castShadow;
      mesh.receiveShadow = false;
      this.disposables.push(mesh);

      let i = 0;
      for (const part of instanceParts) {
        // Floor the radius so very thin bars still read; dia is in metres.
        const r = Math.max(0.005, (part.dia ?? 0.02) / 2);
        forEachSegment(part, (ax, ay, az, bx, by, bz) => {
          dir.set(bx - ax, by - ay, bz - az);
          const len = dir.length();
          if (len < 1e-5) return; // coincident endpoints: nothing to extrude
          quat.setFromUnitVectors(up, dir.divideScalar(len));
          mid.set((ax + bx) / 2, (ay + by) / 2, (az + bz) / 2);
          scale.set(r, len, r);
          mesh.setMatrixAt(i++, matrix.compose(mid, quat, scale));
        });
      }

      // Skipped segments leave the tail of the buffer unwritten, and an unwritten
      // instance matrix is all zeros — which draws as a collapsed cylinder at the
      // origin rather than as nothing. Trim to what was actually filled.
      mesh.count = i;
      mesh.instanceMatrix.needsUpdate = true;
      mesh.computeBoundingSphere();
      group.add(mesh);
    }

    return group.children.length > 0 ? group : null;
  }

  /**
   * Build one bucket's parts as MITRED SWEPT tubes: one ring of `seg` vertices
   * per polyline vertex, in the plane bisecting the two legs that meet there and
   * shared by both quad strips, so a bend is a continuous tube instead of two
   * butt-jointed cylinders with a wedge of background outside the corner. The
   * maths is in RebarSweep.ts.
   *
   * Two passes: measure every polyline, allocate the three buffers to the exact
   * size, then write. `sweepPolyline` runs the same joint arithmetic in both, so
   * the sizes cannot disagree — the alternative, growable arrays, is what makes
   * the naive per-segment-geometry build ask for gigabytes.
   *
   * Normals are analytic (the radial direction, bisected at a joint), so there is
   * no `computeVertexNormals()` here and no shading break where two legs meet.
   * No UVs either, matching the instanced prototype.
   *
   * EVERY run is capped, bars included. A cap here closes a bar END — `seg`
   * vertices and `seg - 2` triangles, twice per bar, against the hundreds of
   * segments the bar itself costs — where the instanced prototype could only
   * close two ends by carrying caps on every segment. A closed loop gets none, a
   * loop having no ends. See the prototype comment in the caller for the rule
   * this replaces.
   */
  private buildSweptMesh(
    parts: BridgePart[],
    seg: number,
    mat: THREE.Material,
    castShadow: boolean,
    withUv: boolean,
  ): THREE.Mesh | null {
    if (!parts.length) return null;

    // Holds each kept vertex's float offset into the part's own positions, so a
    // polyline is walked without copying it out. Grown geometrically and reused
    // across every polyline in the bucket.
    let idx = new Int32Array(256);
    let vertexCount = 0;
    let triangleCount = 0;

    const walk = (out: SweepOut | null): void => {
      for (const part of parts) {
        // Floor the radius so very thin bars still read; dia is in metres.
        const radius = Math.max(0.005, (part.dia ?? 0.02) / 2);
        const pos = part.positions;
        forEachPolyline(part, (base, count) => {
          forEachFiniteRun(pos, base, count, (runBase, runCount) => {
            if (idx.length < runCount) idx = new Int32Array(Math.max(runCount, idx.length * 2));
            const n = dedupePolyline(pos, runBase, runCount, idx);
            if (n < 2) return;
          // A tie or stirrup arrives with its last vertex on top of its first;
          // drop the repeat and sweep the rest as a loop, so the closing corner
          // is mitred like the other three.
            const closed = isClosedLoop(pos, idx, n);
            const size = sweepPolyline(
              pos,
              idx,
              closed ? n - 1 : n,
              closed,
              radius,
              seg,
              true, // cap the run ends: see the note on this method
              withUv,
              out,
            );
            if (!out) {
              vertexCount += size.vertices;
              triangleCount += size.triangles;
            }
          });
        });
      }
    };

    walk(null);
    if (vertexCount === 0 || triangleCount === 0) return null;

    const out: SweepOut = {
      positions: new Float32Array(vertexCount * 3),
      normals: new Float32Array(vertexCount * 3),
      indices: new Uint32Array(triangleCount * 3),
      uvs: withUv ? new Float32Array(vertexCount * 2) : null,
      v: 0,
      i: 0,
    };
    walk(out);

    if (out.v !== vertexCount || out.i !== triangleCount * 3) {
      // Would leave zeroed vertices drawing triangles to the origin — the same
      // failure the instanced path trims for with `mesh.count = i`.
      console.warn(
        `[BridgeScene] swept tube wrote ${out.v}/${vertexCount} vertices and ` +
          `${out.i}/${triangleCount * 3} indices`,
      );
    }

    const geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.BufferAttribute(out.positions, 3));
    geom.setAttribute('normal', new THREE.BufferAttribute(out.normals, 3));
    if (out.uvs) geom.setAttribute('uv', new THREE.BufferAttribute(out.uvs, 2));
    geom.setIndex(new THREE.BufferAttribute(out.indices, 1));
    geom.computeBoundingSphere();
    this.disposables.push(geom);

    // THREE.Mesh has no dispose() — only its geometry and material do, and both
    // are already registered (the material once per bucket, by the caller).
    const mesh = new THREE.Mesh(geom, mat);
    // applyOpacity keys off this name to keep reinforcement solid behind
    // transparent concrete.
    mesh.name = 'Reinforcement';
    mesh.castShadow = castShadow;
    mesh.receiveShadow = false;
    return mesh;
  }

  /**
   * Merge a set of parts into one mesh. Positions are concatenated into a single
   * non-indexed buffer (so computeVertexNormals yields per-face flat normals —
   * the crisp structural look), and each part's colour is written per-vertex.
   * The transparent variant uses an RGBA colour attribute so per-part opacity
   * survives, with a transparent/no-depth-write material.
   */
  private buildMergedMesh(
    parts: BridgePart[],
    transparent: boolean,
    texture: 'concrete' | 'asphalt' | 'grass' | null = null,
    darken = 1,
  ): THREE.Mesh | null {
    let floatCount = 0;
    for (const p of parts) floatCount += p.positions.length;
    if (floatCount === 0) return null;

    const vertexCount = floatCount / 3;
    const comps = transparent ? 4 : 3;
    const positions = new Float32Array(floatCount);
    // In Texture mode the map drives the look, so skip per-vertex colour.
    const textured = texture !== null;
    const colors = textured ? null : new Float32Array(vertexCount * comps);

    const c = new THREE.Color();
    let pi = 0;
    let ci = 0;
    for (const part of parts) {
      positions.set(part.positions, pi);
      pi += part.positions.length;

      if (!colors) continue;
      const a = THREE.MathUtils.clamp(part.opacity ?? 1, 0.05, 1);
      const verts = part.positions.length / 3;
      const vc = part.vcolors;

      if (vc && vc.length >= verts * 3) {
        // Same sRGB → working-space conversion the hex path gets below, so a
        // ramp colour lands where the identical hex would have.
        for (let k = 0; k < verts; k++) {
          c.setRGB(vc[k * 3], vc[k * 3 + 1], vc[k * 3 + 2], THREE.SRGBColorSpace);
          if (darken !== 1) c.multiplyScalar(darken);
          colors[ci++] = c.r;
          colors[ci++] = c.g;
          colors[ci++] = c.b;
          if (transparent) colors[ci++] = a;
        }
        continue;
      }

      // new THREE.Color(hex) is already in the renderer's working (linear) space
      // via ColorManagement, matching how a material.color would have looked.
      c.set(part.color || '#cfcfcf');
      if (darken !== 1) c.multiplyScalar(darken);
      for (let k = 0; k < verts; k++) {
        colors[ci++] = c.r;
        colors[ci++] = c.g;
        colors[ci++] = c.b;
        if (transparent) colors[ci++] = a;
      }
    }

    const geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    if (textured) {
      // World-space (triplanar) UVs so the concrete map tiles at a real-world
      // scale and doesn't stretch on vertical faces (columns, girder webs).
      geom.setAttribute('uv', new THREE.BufferAttribute(computeTriplanarUV(positions), 2));
    } else if (colors) {
      geom.setAttribute('color', new THREE.BufferAttribute(colors, comps));
    }
    geom.computeVertexNormals();
    // Round parts get creased smooth normals in place of their per-face ones.
    let smoothed = false;
    {
      const normals = geom.getAttribute('normal').array as Float32Array;
      let v0 = 0;
      for (const part of parts) {
        const n = part.positions.length / 3;
        if (part.smooth) { smoothCreased(positions, normals, v0, n, SMOOTH_CREASE_DEG); smoothed = true; }
        v0 += n;
      }
    }
    geom.computeBoundingBox();
    this.disposables.push(geom);

    const mat = textured
      ? new THREE.MeshStandardMaterial({
          map: surfaceTexture(texture!),
          bumpMap: surfaceTexture(texture!),
          // Grass carries no relief: a bump map of ground cover shades per texel, and at this
          // band's size every texel is sub-pixel, so it reads as shimmer rather than texture.
          bumpScale: texture === 'grass' ? 0 : 0.4,
          color: 0xffffff,
          metalness: 0.0,
          roughness: texture === 'grass' ? 1.0 : texture === 'asphalt' ? 0.98 : 0.95,
          side: THREE.DoubleSide,
          // Explicit normals throughout once a part is smoothed: computeVertexNormals
          // already gave every other part its face normal, which shades the same.
          flatShading: !smoothed,
          polygonOffset: true,
          polygonOffsetFactor: 1,
          polygonOffsetUnits: 1,
        })
      : new THREE.MeshStandardMaterial({
          vertexColors: true,
          metalness: 0.0,
          roughness: 0.85,
          side: THREE.DoubleSide,
          // Explicit normals throughout once a part is smoothed: computeVertexNormals
          // already gave every other part its face normal, which shades the same.
          flatShading: !smoothed,
          transparent,
          depthWrite: !transparent,
          // Push surfaces back a hair so GeometryOutline edge lines (drawn at the
          // same depth) sit crisply on top instead of z-fighting.
          polygonOffset: true,
          polygonOffsetFactor: 1,
          polygonOffsetUnits: 1,
        });
    this.disposables.push(mat);

    const mesh = new THREE.Mesh(geom, mat);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    return mesh;
  }

  /** World-space bounding box of all current geometry (empty if none). */
  public getBounds(): THREE.Box3 {
    return new THREE.Box3().setFromObject(this.root);
  }

  /** True once at least one part has been added. */
  public get hasContent(): boolean {
    return this.root.children.length > 0;
  }

  /** Drop all current meshes and free their GPU resources. */
  public clear(): void {
    if (this.highlightMesh) {
      this.highlightMesh.geometry.dispose();
      (this.highlightMesh.material as THREE.Material).dispose();
      this.highlightMesh = null;
    }
    this.selectable = [];
    this.outlineable = [];
    this.selectedIds = [];
    this.pickMesh = null;
    this.faceObjIds = [];
    this.diagramPickMesh = null;
    this.diagramFaceIds = [];
    // Both hulls are built from `selectable` and are stale the moment that list
    // is dropped. The instances survive (each owns an OnFrame subscription);
    // only their geometry and their place in the graph go.
    this.hull?.clear();
    this.hullAll?.clear();
    if (this.hullAll?.group.parent) this.root.remove(this.hullAll.group);
    this.clearCreases();
    for (const child of [...this.root.children]) this.root.remove(child);
    for (const d of this.disposables) d.dispose();
    this.disposables.length = 0;
  }
}

// ---------------------------------------------------------------------------
//  Concrete texture (Color By → Texture)
// ---------------------------------------------------------------------------

/** Lazily-built, shared surface textures, keyed by kind. Not added to a scene's
 *  disposables — they're singletons reused across reloads (material.dispose()
 *  never disposes its textures, so sharing is safe). */
const _texCache: Record<string, THREE.CanvasTexture> = {};

/**
 * Lazily-generated steel maps for Texture mode, keyed by surface and map kind.
 * Shared singletons, like the concrete and asphalt ones below.
 *
 * GENERATED, not loaded. This started as the legacy WinUI canvas's asset set
 * (`armatura_low_DefaultMaterial_*`, a photogrammetry bake of one real bar) and that
 * was the wrong shape of thing: a scan bake carries its own UV layout, so wrapped
 * onto a smooth swept cylinder it reproduced neither the rib pitch nor the rib
 * direction, and no amount of retiling fixed it. Deformed bar is standardised
 * geometry — see the rib constants above — so it is both cheaper and more faithful
 * to draw it than to photograph it.
 *
 * What that bought: correct rib pitch at every diameter, a pattern that tiles by
 * construction on all four sides, ~8 KB of canvas instead of 676 KB of PNG, no image
 * assets to deploy or mirror, and no licence question about a bundled asset pack.
 */
const _steelTexCache: Record<string, THREE.CanvasTexture> = {};

function steelTexture(kind: 'rebar' | 'duct', map: 'color' | 'normal'): THREE.CanvasTexture {
  const key = `${kind}|${map}`;
  const cached = _steelTexCache[key];
  if (cached) return cached;

  // No repeat: the sweep hands over `u` in turns and `v` in rib pitches, and the
  // pattern is drawn to suit. Scaling here would multiply the spines.
  const tex = map === 'color' ? makeSteelColorTexture(kind) : makeSteelNormalTexture(kind);
  _steelTexCache[key] = tex;
  return tex;
}

function surfaceTexture(kind: 'concrete' | 'asphalt' | 'grass'): THREE.CanvasTexture {
  if (_texCache[kind]) return _texCache[kind];
  const tex =
    kind === 'asphalt' ? makeAsphaltTexture() : kind === 'grass' ? makeGrassTexture() : makeConcreteTexture();
  _texCache[kind] = tex;
  return tex;
}

/**
 * Shape of a deformed bar's surface, drawn rather than photographed.
 *
 * The target is a RIB CAGE: two straight longitudinal ribs - spines - on opposite
 * sides of the bar, with short transverse lugs angling off each side of a spine and
 * stopping short of it, the lean reversing across the spine. That is what hot-rolled
 * deformed bar looks like, and it is why a photograph of one bar could never work
 * here: a scan bake carries its own layout, so wrapped on a smooth cylinder it gave
 * continuous bands crossing the spines, and at the wrong count.
 *
 * `u` is turns (exactly one per circuit) and `v` is rib pitches, so the pattern knows
 * its own aspect ratio - RIB_ASPECT below - and can draw a real lean angle in it.
 */
const RIB_ASPECT = Math.PI / RIB_PITCH_FACTOR; // circumference / rib pitch, a constant

/** Longitudinal ribs (spines) around the bar. Two, on opposite sides, as on real bar. */
const SPINE_COUNT = 2;
/** Spine width, as a fraction of the face between spines. */
const SPINE_WIDTH = 0.20;
const SPINE_HEIGHT = 1.0;

/** Transverse lug lean, in degrees FROM THE BAR AXIS. Real bar runs 45-70. */
const RIB_LEAN_DEG = 68;
/** Lug width, as a fraction of one rib pitch. */
const RIB_WIDTH = 0.24;
/** How much of the face either side of a spine the lugs keep clear of, and the length
 *  over which they then fade in — together these give the lug its tapered ends. */
const RIB_END_GAP = 0.07;
const RIB_TAPER = 0.16;

/**
 * Reverse the lug lean on each side of a spine, so they meet it in a chevron.
 *
 * Seam-safe whatever the lean, unlike the earlier version where it was not: the lugs
 * now fade to nothing before they reach a spine, and every flip boundary IS a spine,
 * so no lug ever spans one. That also frees the lean from having to be a whole number.
 */
const RIB_HERRINGBONE = true;

/** Corrugation of a duct: broad deep rings, no spine. Fraction of one pitch. */
const DUCT_RIB_WIDTH = 0.45;

/** Rib height as a fraction of one rib pitch. Real bar runs about 0.04-0.05 of the
 *  diameter, which at a 0.5 pitch factor is 0.08-0.10 of a pitch; a little over that
 *  reads better once the crest is flat, because the flank is what is actually seen. */
const RIB_RELIEF = 0.13;

/**
 * Fraction of a rib's half-width that sits at FULL height — the flat crest. The
 * remainder is the flank.
 *
 * A real rib is roughly trapezoidal: a definite top with steep sides. Smoothstepping
 * across the whole half-width instead makes a rounded hill, which is what made the
 * first version read as soft. Pushing the shoulder into the outer half gives the crest
 * an edge for the light to catch.
 */
const RIB_CREST_FLAT = 0.5;

/** 0..1 ridge profile across a band of half-width `w`: a flat crest of
 *  {@link RIB_CREST_FLAT}, then a smoothstep flank, then zero outside. */
function ridge(x: number, w: number): number {
  const d = Math.min(x, 1 - x); // distance to the band centre at 0, wrapped
  if (d >= w) return 0;
  return smoothstep01((1 - d / w) / (1 - RIB_CREST_FLAT));
}

function smoothstep01(p: number): number {
  const c = p < 0 ? 0 : p > 1 ? 1 : p;
  return c * c * (3 - 2 * c);
}

/** Surface height in [0,1]. `u` is turns around the tube, `v` is rib pitches along. */
function ribHeight(u: number, v: number, kind: 'rebar' | 'duct'): number {
  if (kind === 'duct') {
    // Helically wound corrugation: one broad, deep ring per pitch, leaning slightly
    // so it reads as wound rather than stacked. No spine — a duct has none.
    const p = v - 0.35 * u;
    return ridge(p - Math.floor(p), DUCT_RIB_WIDTH);
  }

  // Which face of the bar, and where across it. A face runs spine to spine.
  const face = Math.floor(u * SPINE_COUNT);
  const fu = u * SPINE_COUNT - face;
  const lean = RIB_HERRINGBONE && face % 2 !== 0 ? -1 : 1;

  // The spine, at fu = 0 — which is also the u seam and every flip boundary.
  const spine = SPINE_HEIGHT * ridge(fu, SPINE_WIDTH);

  // The lug. Slope in (u,v) is set by the lean angle THROUGH the aspect ratio, so 68
  // degrees from the axis is 68 degrees on the bar and not just in the image.
  const slope = RIB_ASPECT / Math.tan((RIB_LEAN_DEG * Math.PI) / 180);
  const phase = v - lean * slope * u;
  const band = ridge(phase - Math.floor(phase), RIB_WIDTH);

  // Tapered ends: nothing within RIB_END_GAP of a spine, fading in over RIB_TAPER.
  const edge = Math.min(fu, 1 - fu);
  const span = edge <= RIB_END_GAP ? 0 : smoothstep01((edge - RIB_END_GAP) / RIB_TAPER);

  return Math.max(band * span, spine);
}

/**
 * Normal map for a steel surface, differentiated from {@link ribHeight}.
 *
 * Built from a height field rather than drawn directly, for two reasons: the ribs get
 * a rounded flank for free, and central differences taken with WRAPPING indices make
 * the map seamless by construction rather than by eye — which matters because the
 * sweep's tile meets itself on all four sides.
 */
function makeSteelNormalTexture(kind: 'rebar' | 'duct'): THREE.CanvasTexture {
  // 512 rather than 256: a bar fills the screen when someone zooms into a cage, and
  // the ribs are the whole point of the map. Generated, so the only cost is ~1 MB of
  // canvas at load and nothing at all to deploy.
  const size = 512;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const img = ctx.createImageData(size, size);
  const d = img.data;

  const h = new Float32Array(size * size);
  for (let j = 0; j < size; j++) {
    for (let i = 0; i < size; i++) h[j * size + i] = ribHeight(i / size, j / size, kind);
  }

  const at = (i: number, j: number): number =>
    h[((j + size) % size) * size + ((i + size) % size)];

  for (let j = 0; j < size; j++) {
    for (let i = 0; i < size; i++) {
      // Height in rib-pitch units, so the slopes are real gradients. du is divided by
      // RIB_ASPECT because one unit of u (a whole turn) is that many pitches of arc —
      // without it the ribs would lean correctly but shade as if the bar were flat.
      const du =
        ((at(i + 1, j) - at(i - 1, j)) * 0.5 * size * RIB_RELIEF) / RIB_ASPECT;
      const dv = (at(i, j + 1) - at(i, j - 1)) * 0.5 * size * RIB_RELIEF;
      // Tangent-space normal: the slope of the height field, negated, with z up.
      const len = Math.sqrt(du * du + dv * dv + 1);
      const o = (j * size + i) * 4;
      d[o] = Math.round(((-du / len) * 0.5 + 0.5) * 255);
      d[o + 1] = Math.round(((-dv / len) * 0.5 + 0.5) * 255);
      d[o + 2] = Math.round((1 / len) * 0.5 * 255 + 127.5);
      d[o + 3] = 255;
    }
  }

  ctx.putImageData(img, 0, 0);
  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  // A normal map is raw vectors, NOT colour — leaving it in sRGB would bend every
  // normal toward z and flatten the ribs.
  // A bar is nearly always seen at a grazing angle, which is exactly where isotropic
  // mip filtering smears a rib pattern into mush.
  tex.anisotropy = 8;
  return tex;
}

/**
 * Base colour for a steel surface: dark mill scale, with the rib crests picked out
 * slightly lighter where the scale rubs off in handling, plus fine grain.
 *
 * Mill scale is a bluish-black iron oxide, which is why the base is a cool dark grey
 * and not a rust colour — see BridgeGeometryExporter.RebarHex for the same argument.
 */
function makeSteelColorTexture(kind: 'rebar' | 'duct'): THREE.CanvasTexture {
  const size = 512;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const img = ctx.createImageData(size, size);
  const d = img.data;

  // Duct is HDPE or galvanised, so a touch lighter and more neutral than bar scale.
  const base = kind === 'duct' ? [64, 66, 70] : [47, 50, 55];
  // Rib crests read lighter than the valleys: that is where handling rubs the mill
  // scale back. Raised with the sharper profile — a flat crest gives it somewhere
  // definite to sit instead of fading across the whole rib.
  const crest = 38;

  for (let j = 0; j < size; j++) {
    for (let i = 0; i < size; i++) {
      const lift = ribHeight(i / size, j / size, kind) * crest;
      const grain = (Math.random() - 0.5) * 10;
      const o = (j * size + i) * 4;
      for (let c = 0; c < 3; c++) {
        const value = base[c] + lift + grain;
        d[o + c] = value < 0 ? 0 : value > 255 ? 255 : value;
      }
      d[o + 3] = 255;
    }
  }

  ctx.putImageData(img, 0, 0);
  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return tex;
}

/** Procedural concrete: a mid-grey base with fine per-pixel speckle and a few
 *  soft blotches — no image asset needed, tiles seamlessly enough at scale. */
function makeConcreteTexture(): THREE.CanvasTexture {
  return makeNoiseTexture('#b7b6b0', 34, 28, 0.05, 95, 60);
}

/** Procedural asphalt: a dark near-black base with coarse, high-contrast grain
 *  (the aggregate look) and a few darker blotches. */
function makeAsphaltTexture(): THREE.CanvasTexture {
  return makeNoiseTexture('#3a3a3d', 46, 22, 0.10, 30, 40);
}

/**
 * Procedural grass, for the ground profile band.
 *
 * Not makeNoiseTexture: that varies all three channels together, which on a green base gives
 * light-and-dark green — mottled paint, not turf. Grass reads as grass because its HUE moves,
 * yellow-green through to blue-green, so that is what this varies.
 *
 * Deliberately ISOTROPIC and low-frequency, which is the opposite of what it first was. Drawn
 * blades looked right in isolation and wrong on the model: the band is 70 m x 230 m and is never
 * viewed from close enough for a blade to cover a pixel, so a 0.6-1.5 px stroke only ever
 * aliases. Worse, computeTriplanarUV gives EVERY TRIANGLE its own planar projection, so any
 * directional content advertises that orientation — the band came out combed into diagonal
 * corduroy that changed direction at each triangle seam. Nothing with a direction or a
 * sub-pixel feature can live in this texture; what survives minification is hue variation at a
 * scale of tens of centimetres, and that is all this draws.
 */
function makeGrassTexture(): THREE.CanvasTexture {
  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;

  // Base field.
  ctx.fillStyle = '#4e7a3c';
  ctx.fillRect(0, 0, size, size);

  // Soft clumps, at two scales. Round and blurred, so the field has no grain direction for a
  // per-triangle UV to reveal, and nothing small enough to alias once minified. Drawn at the
  // four tile offsets as well so a clump crossing an edge continues on the opposite one.
  const clump = (count: number, rMin: number, rSpan: number, alpha: number) => {
    for (let k = 0; k < count; k++) {
      const x = Math.random() * size;
      const y = Math.random() * size;
      const r = rMin + Math.random() * rSpan;
      const h = 84 + Math.random() * 36;          // yellow-green .. blue-green
      const s = 26 + Math.random() * 24;
      const l = 24 + Math.random() * 18;
      for (const [ox, oy] of [[0, 0], [size, 0], [0, size], [size, size]] as const) {
        const cx = ((x + ox) % size) - (ox ? size : 0);
        const cy = ((y + oy) % size) - (oy ? size : 0);
        const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
        g.addColorStop(0, `hsla(${h}, ${s}%, ${l}%, ${alpha})`);
        g.addColorStop(1, `hsla(${h}, ${s}%, ${l}%, 0)`);
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  };
  clump(26, 34, 46, 0.45);     // broad tonal drift
  clump(70, 12, 20, 0.30);     // patchiness within it

  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return tex;
}

/**
 * Build a tiling noise texture: a flat base colour, per-pixel grey grain of
 * amplitude <paramref/> grainAmp, and a scatter of soft circular blotches.
 */
function makeNoiseTexture(
  baseHex: string,
  grainAmp: number,
  blotches: number,
  blotchAlpha: number,
  blotchBase: number,
  blotchRange: number,
): THREE.CanvasTexture {
  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;

  ctx.fillStyle = baseHex;
  ctx.fillRect(0, 0, size, size);

  const img = ctx.getImageData(0, 0, size, size);
  const d = img.data;
  const clamp = (v: number) => (v < 0 ? 0 : v > 255 ? 255 : v);
  for (let i = 0; i < d.length; i += 4) {
    const n = (Math.random() - 0.5) * grainAmp;
    d[i] = clamp(d[i] + n);
    d[i + 1] = clamp(d[i + 1] + n);
    d[i + 2] = clamp(d[i + 2] + n);
  }
  ctx.putImageData(img, 0, 0);

  for (let k = 0; k < blotches; k++) {
    const x = Math.random() * size;
    const y = Math.random() * size;
    const r = 6 + Math.random() * 26;
    const g = blotchBase + Math.floor(Math.random() * blotchRange);
    ctx.fillStyle = `rgba(${g},${g},${g},${blotchAlpha})`;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }

  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return tex;
}

/** Per-triangle planar UVs projected on the dominant face-normal axis (CPU
 *  triplanar) so the concrete tiles at world scale on every orientation.
 *  Geometry is non-indexed (3 verts per triangle), so each tri is independent. */
/**
 * Creased smooth normals for vertices [v0, v0+n) of a non-indexed triangle buffer
 * whose normals are still per-face: each vertex takes the mean of the face normals
 * at its position that lie within `creaseDeg` of its own face's.
 */
function smoothCreased(positions: Float32Array, normals: Float32Array, v0: number, n: number, creaseDeg: number): void {
  const face = normals.slice(v0 * 3, (v0 + n) * 3);
  const cos = Math.cos((creaseDeg * Math.PI) / 180);
  const groups = new Map<string, number[]>();
  const q = (x: number) => Math.round(x * 1e4); // 0.1 mm in metres
  for (let i = 0; i < n; i++) {
    const k = (v0 + i) * 3;
    const key = `${q(positions[k])},${q(positions[k + 1])},${q(positions[k + 2])}`;
    let g = groups.get(key);
    if (!g) groups.set(key, (g = []));
    g.push(i);
  }
  for (const g of groups.values()) {
    for (const i of g) {
      const ax = face[i * 3], ay = face[i * 3 + 1], az = face[i * 3 + 2];
      let sx = 0, sy = 0, sz = 0;
      for (const j of g) {
        const bx = face[j * 3], by = face[j * 3 + 1], bz = face[j * 3 + 2];
        if (ax * bx + ay * by + az * bz >= cos) { sx += bx; sy += by; sz += bz; }
      }
      const len = Math.hypot(sx, sy, sz);
      if (len > 0) {
        const k = (v0 + i) * 3;
        normals[k] = sx / len; normals[k + 1] = sy / len; normals[k + 2] = sz / len;
      }
    }
  }
}

function computeTriplanarUV(positions: Float32Array): Float32Array {
  const scale = 0.8; // texture repeats per ~1.25 m
  const uv = new Float32Array((positions.length / 3) * 2);

  for (let t = 0; t < positions.length; t += 9) {
    const ax = positions[t], ay = positions[t + 1], az = positions[t + 2];
    const bx = positions[t + 3], by = positions[t + 4], bz = positions[t + 5];
    const cx = positions[t + 6], cy = positions[t + 7], cz = positions[t + 8];

    // Face normal (unnormalized) → dominant axis.
    const ux = bx - ax, uy = by - ay, uz = bz - az;
    const vx = cx - ax, vy = cy - ay, vz = cz - az;
    const nx = Math.abs(uy * vz - uz * vy);
    const ny = Math.abs(uz * vx - ux * vz);
    const nz = Math.abs(ux * vy - uy * vx);

    const o = (t / 3) * 2;
    for (let j = 0; j < 3; j++) {
      const px = positions[t + j * 3];
      const py = positions[t + j * 3 + 1];
      const pz = positions[t + j * 3 + 2];
      let s: number, r: number;
      if (nx >= ny && nx >= nz) { s = pz; r = py; }      // X-facing → ZY
      else if (ny >= nx && ny >= nz) { s = px; r = pz; } // Y-facing → XZ
      else { s = px; r = py; }                            // Z-facing → XY
      uv[o + j * 2] = s * scale;
      uv[o + j * 2 + 1] = r * scale;
    }
  }
  return uv;
}
